import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  CalendarIcon,
  Clock,
  MapPin,
  CreditCard,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Star,
  Shield,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import ServiceProviderBadges from './ServiceProviderBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Time slots for booking
const TIME_SLOTS = [
  '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM',
  '02:00 PM', '02:30 PM', '03:00 PM', '03:30 PM',
  '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
];

/**
 * ServiceBookingModal
 * Fixed-Price Booking Flow
 * Steps: 1. Select Service -> 2. Enter Details -> 3. Schedule -> 4. Payment
 */
const ServiceBookingModal = ({
  open,
  onClose,
  service,
  selectedFixedService = null,
  onSubmit,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bookingData, setBookingData] = useState({
    service_id: selectedFixedService?.id || '',
    service_name: selectedFixedService?.name || '',
    service_price: selectedFixedService?.price || 0,
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    notes: '',
    preferred_date: null,
    preferred_time: '',
    delivery_option: 'pickup', // 'pickup' | 'delivery'
    delivery_address: '',
    payment_method: 'cash', // 'cash' | 'card' | 'online'
  });

  if (!service) return null;

  const {
    provider_name,
    provider_image,
    rating = 0,
    review_count = 0,
    verification_level = 0,
    achievements = [],
    fixed_price_services = [],
    currency = 'Rs',
  } = service;

  const imageUrl = provider_image
    ? (provider_image.startsWith('http') ? provider_image : `${API_URL}${provider_image}`)
    : 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit?.({
        ...bookingData,
        provider_id: service.id,
        booking_type: 'fixed_price',
      });
      setStep(5); // Success step
    } catch (error) {
      console.error('Booking failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return bookingData.service_id;
      case 2:
        return bookingData.customer_name && bookingData.customer_phone;
      case 3:
        return bookingData.preferred_date && bookingData.preferred_time;
      case 4:
        return bookingData.payment_method;
      default:
        return true;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Provider Header */}
        <DialogHeader className="pb-4 border-b">
          <div className="flex items-center gap-3">
            <img
              src={imageUrl}
              alt={provider_name}
              className="w-12 h-12 rounded-full object-cover border-2 border-purple-200"
            />
            <div>
              <DialogTitle className="text-lg">{provider_name}</DialogTitle>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 text-yellow-500 fill-yellow-500" />
                  <span className="text-sm font-medium">{rating.toFixed(1)}</span>
                </div>
                <ServiceProviderBadges
                  verificationLevel={verification_level}
                  achievements={achievements}
                  maxBadges={2}
                />
              </div>
            </div>
          </div>
        </DialogHeader>

        {/* Progress Steps */}
        {step <= 4 && (
          <div className="flex items-center justify-between px-2 py-4">
            {['Service', 'Details', 'Schedule', 'Payment'].map((label, index) => (
              <div key={label} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step > index + 1
                      ? 'bg-green-500 text-white'
                      : step === index + 1
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step > index + 1 ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                </div>
                {index < 3 && (
                  <div
                    className={`w-12 h-1 mx-1 ${
                      step > index + 1 ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Select Service */}
        {step === 1 && (
          <div className="space-y-4 py-4">
            <DialogDescription>
              Select the service you need
            </DialogDescription>
            <div className="space-y-2">
              {fixed_price_services.map((svc) => (
                <div
                  key={svc.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    bookingData.service_id === svc.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                  onClick={() =>
                    setBookingData({
                      ...bookingData,
                      service_id: svc.id,
                      service_name: svc.name,
                      service_price: svc.price,
                    })
                  }
                  data-testid={`select-service-${svc.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{svc.name}</h4>
                      <p className="text-sm text-gray-500">{svc.description}</p>
                      {svc.duration && (
                        <p className="text-xs text-gray-400 mt-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          {svc.duration}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-600">
                        {currency} {svc.price?.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Customer Details */}
        {step === 2 && (
          <div className="space-y-4 py-4">
            <DialogDescription>
              Enter your contact details
            </DialogDescription>
            <div className="space-y-4">
              <div>
                <Label htmlFor="customer_name">Full Name *</Label>
                <Input
                  id="customer_name"
                  value={bookingData.customer_name}
                  onChange={(e) =>
                    setBookingData({ ...bookingData, customer_name: e.target.value })
                  }
                  placeholder="Enter your name"
                  data-testid="booking-customer-name"
                />
              </div>
              <div>
                <Label htmlFor="customer_phone">Phone Number *</Label>
                <Input
                  id="customer_phone"
                  value={bookingData.customer_phone}
                  onChange={(e) =>
                    setBookingData({ ...bookingData, customer_phone: e.target.value })
                  }
                  placeholder="Enter phone number"
                  data-testid="booking-customer-phone"
                />
              </div>
              <div>
                <Label htmlFor="customer_email">Email (Optional)</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={bookingData.customer_email}
                  onChange={(e) =>
                    setBookingData({ ...bookingData, customer_email: e.target.value })
                  }
                  placeholder="Enter email"
                />
              </div>
              <div>
                <Label htmlFor="notes">Special Instructions (Optional)</Label>
                <Textarea
                  id="notes"
                  value={bookingData.notes}
                  onChange={(e) =>
                    setBookingData({ ...bookingData, notes: e.target.value })
                  }
                  placeholder="Any special requirements..."
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Schedule */}
        {step === 3 && (
          <div className="space-y-4 py-4">
            <DialogDescription>
              Choose your preferred date and time
            </DialogDescription>
            <div className="space-y-4">
              <div>
                <Label>Preferred Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="booking-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {bookingData.preferred_date ? (
                        format(bookingData.preferred_date, 'PPP')
                      ) : (
                        <span className="text-gray-500">Select date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={bookingData.preferred_date}
                      onSelect={(date) =>
                        setBookingData({ ...bookingData, preferred_date: date })
                      }
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Preferred Time *</Label>
                <Select
                  value={bookingData.preferred_time}
                  onValueChange={(value) =>
                    setBookingData({ ...bookingData, preferred_time: value })
                  }
                >
                  <SelectTrigger data-testid="booking-time-select">
                    <SelectValue placeholder="Select time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Delivery Option</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    type="button"
                    variant={bookingData.delivery_option === 'pickup' ? 'default' : 'outline'}
                    className={bookingData.delivery_option === 'pickup' ? 'bg-purple-600' : ''}
                    onClick={() =>
                      setBookingData({ ...bookingData, delivery_option: 'pickup' })
                    }
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Pick Up
                  </Button>
                  <Button
                    type="button"
                    variant={bookingData.delivery_option === 'delivery' ? 'default' : 'outline'}
                    className={bookingData.delivery_option === 'delivery' ? 'bg-purple-600' : ''}
                    onClick={() =>
                      setBookingData({ ...bookingData, delivery_option: 'delivery' })
                    }
                  >
                    Delivery
                  </Button>
                </div>
              </div>
              {bookingData.delivery_option === 'delivery' && (
                <div>
                  <Label htmlFor="delivery_address">Delivery Address</Label>
                  <Textarea
                    id="delivery_address"
                    value={bookingData.delivery_address}
                    onChange={(e) =>
                      setBookingData({ ...bookingData, delivery_address: e.target.value })
                    }
                    placeholder="Enter delivery address"
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Payment */}
        {step === 4 && (
          <div className="space-y-4 py-4">
            <DialogDescription>
              Choose payment method
            </DialogDescription>
            
            {/* Order Summary */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">Order Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{bookingData.service_name}</span>
                  <span>{currency} {bookingData.service_price?.toLocaleString()}</span>
                </div>
                {bookingData.delivery_option === 'delivery' && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery</span>
                    <span>{currency} 300</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-2">
                  <span>Total</span>
                  <span className="text-purple-600">
                    {currency} {(
                      bookingData.service_price + 
                      (bookingData.delivery_option === 'delivery' ? 300 : 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Payment Methods */}
            <div className="space-y-2">
              {[
                { id: 'cash', label: 'Pay on Pickup/Delivery', icon: '💵' },
                { id: 'card', label: 'Credit/Debit Card', icon: '💳' },
                { id: 'online', label: 'Online Payment', icon: '🌐' },
              ].map((method) => (
                <div
                  key={method.id}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    bookingData.payment_method === method.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                  onClick={() =>
                    setBookingData({ ...bookingData, payment_method: method.id })
                  }
                  data-testid={`payment-method-${method.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{method.icon}</span>
                    <span className="font-medium">{method.label}</span>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Security Note */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Shield className="h-4 w-4 text-green-500" />
              <span>Your payment is protected by Zeilian Guarantee</span>
            </div>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Booking Confirmed!</h3>
            <p className="text-gray-600 mb-4">
              Your booking has been confirmed. The provider will contact you shortly.
            </p>
            <div className="p-4 bg-gray-50 rounded-lg text-left text-sm">
              <p><strong>Service:</strong> {bookingData.service_name}</p>
              <p><strong>Date:</strong> {bookingData.preferred_date && format(bookingData.preferred_date, 'PPP')}</p>
              <p><strong>Time:</strong> {bookingData.preferred_time}</p>
              <p><strong>Total:</strong> {currency} {(
                bookingData.service_price + 
                (bookingData.delivery_option === 'delivery' ? 300 : 0)
              ).toLocaleString()}</p>
            </div>
            <Button
              className="mt-6 bg-purple-600 hover:bg-purple-700"
              onClick={onClose}
              data-testid="booking-success-close"
            >
              Done
            </Button>
          </div>
        )}

        {/* Footer Actions */}
        {step <= 4 && (
          <DialogFooter className="flex justify-between pt-4 border-t">
            {step > 1 ? (
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}
            {step < 4 ? (
              <Button
                className="bg-purple-600 hover:bg-purple-700"
                onClick={handleNext}
                disabled={!canProceed()}
                data-testid="booking-next-step"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleSubmit}
                disabled={loading || !canProceed()}
                data-testid="booking-confirm"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Confirm Booking
                    <CheckCircle2 className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceBookingModal;
